/**
 * Element data model + helpers for the Canva-style canvas editor.
 *
 * A design is a list of absolutely positioned elements inside a 1920x1080 canvas.
 *   element = {
 *     id, kind: "text"|"image"|"shape"|"widget",
 *     x, y, w, h, rotation, z, locked,
 *     // kind-specific props (see below)
 *   }
 */

export const CANVAS_W = 1920;
export const CANVAS_H = 1080;

let _uid = 0;
export function newId(prefix = "el") {
  _uid += 1;
  return `${prefix}-${Date.now().toString(36)}-${_uid}`;
}

export function defaultElement(kind, partial = {}) {
  const base = {
    id: newId(kind),
    kind,
    x: 400, y: 400, w: 600, h: 200,
    rotation: 0,
    z: 1,
    locked: false,
  };
  if (kind === "text") {
    return {
      ...base, w: 800, h: 200,
      text: "Add your text",
      fontFamily: "Outfit",
      fontSize: 96,
      fontWeight: 800,
      italic: false,
      underline: false,
      strike: false,
      color: "#0F172A",
      align: "center",
      lineHeight: 1.1,
      letterSpacing: 0,
      ...partial,
    };
  }
  if (kind === "image") {
    return {
      ...base, w: 600, h: 400,
      src: "",
      fit: "cover",
      opacity: 1,
      borderRadius: 0,
      ...partial,
    };
  }
  // Video element — same footprint as an image by default but rendered as
  // a native <video> element with autoplay/loop/muted/playsInline all on
  // (required for Android WebView + iOS Safari autoplay). Customers add
  // these via the "Upload video" button on the Banners page or by dropping
  // an MP4/WebM into any image slot in the editor.
  if (kind === "video") {
    return {
      ...base, w: 960, h: 540,
      src: "",
      poster: null,
      fit: "contain",
      opacity: 1,
      borderRadius: 0,
      muted: true,
      loop: true,
      autoplay: true,
      playsInline: true,
      controls: false,
      ...partial,
    };
  }
  if (kind === "shape") {
    return {
      ...base, w: 400, h: 400,
      shape: "rect",
      fill: "#0EA5E9",
      stroke: "transparent",
      strokeWidth: 0,
      borderRadius: 0,
      opacity: 1,
      ...partial,
    };
  }
  if (kind === "widget") {
    return {
      ...base, w: 400, h: 200,
      widgetType: "weather",
      widgetConfig: {},
      ...partial,
    };
  }
  return { ...base, ...partial };
}

export const FONT_FAMILIES = [
  { id: "Outfit",            label: "Outfit",            css: "Outfit, sans-serif" },
  { id: "Manrope",           label: "Manrope",           css: "Manrope, sans-serif" },
  { id: "Inter",             label: "Inter",             css: "Inter, sans-serif" },
  { id: "Acme",              label: "Acme",              css: "Acme, sans-serif" },
  { id: "Bebas Neue",        label: "Bebas Neue",        css: "'Bebas Neue', sans-serif" },
  { id: "Playfair Display",  label: "Playfair Display",  css: "'Playfair Display', serif" },
  { id: "Anton",             label: "Anton",             css: "Anton, sans-serif" },
  { id: "Oswald",            label: "Oswald",            css: "Oswald, sans-serif" },
  { id: "Pacifico",          label: "Pacifico",          css: "Pacifico, cursive" },
  { id: "Lobster",           label: "Lobster",           css: "Lobster, cursive" },
  { id: "Dancing Script",    label: "Dancing Script",    css: "'Dancing Script', cursive" },
  { id: "Caveat",            label: "Caveat",            css: "Caveat, cursive" },
];

export function fontCss(family) {
  const f = FONT_FAMILIES.find((x) => x.id === family);
  return f ? f.css : `${family}, sans-serif`;
}

// ---------------------------------------------------------------------------
// Layout → elements converters (so existing polished templates open in the
// canvas editor and become directly editable).
// ---------------------------------------------------------------------------
export function configToElements(config = {}) {
  if (Array.isArray(config.elements) && config.elements.length > 0) {
    return config.elements;
  }
  const layout = config.layout || "default";
  if (layout === "chalkboard_grid")   return chalkboardGridToElements(config);
  if (layout === "burger_combo_kids") return marbleBoardToElements(config);
  if (layout === "promo_card")       return promoCardToElements(config);
  if (layout === "split_layout")     return splitLayoutToElements(config);
  if (layout === "shape_burst")      return shapeBurstToElements(config);
  if (layout === "lifestyle_card")   return lifestyleCardToElements(config);
  if (layout === "menu_grid_modern") return menuGridModernToElements(config);
  if (layout === "signage_board")    return signageBoardToElements(config);
  return defaultToElements(config);
}

function promoCardToElements(c) {
  const accent = c.accent_color || "#0EA5E9";
  const secondary = c.secondary_color || "#FFFFFF";
  const els = [];
  // Full-bleed background
  els.push(defaultElement("shape", { id: "bg", x: 0, y: 0, w: 1920, h: 1080, shape: "rect", fill: accent, locked: false, z: 0 }));
  if (c.banner_image) {
    els.push(defaultElement("image", { id: "bg-photo", x: 0, y: 0, w: 1920, h: 1080, src: c.banner_image, fit: "cover", opacity: 0.55, z: 1 }));
  }
  // Diagonal slash
  els.push(defaultElement("shape", {
    id: "slash", x: -160, y: 240, w: 1100, h: 600, shape: "rect",
    fill: secondary, opacity: 0.95, rotation: -18, z: 2,
  }));
  if (c.logo_text) {
    els.push(defaultElement("text", {
      id: "logo", x: 96, y: 96, w: 600, h: 60,
      text: c.logo_text, fontFamily: "Outfit", fontSize: 28, fontWeight: 700,
      color: "#FFFFFF", align: "left", letterSpacing: 6, z: 5,
    }));
  }
  if (c.badge) {
    els.push(defaultElement("text", {
      id: "badge", x: 1200, y: 90, w: 620, h: 90,
      text: c.badge, fontFamily: "Outfit", fontSize: 42, fontWeight: 900,
      color: accent, align: "center", letterSpacing: 1, z: 5,
    }));
  }
  els.push(defaultElement("text", {
    id: "headline", x: 96, y: 480, w: 1200, h: 240,
    text: c.headline || "Headline", fontFamily: "Outfit", fontSize: 220, fontWeight: 900,
    color: "#FFFFFF", align: "left", letterSpacing: -4, lineHeight: 0.95, z: 5,
  }));
  if (c.subline) {
    els.push(defaultElement("text", {
      id: "subline", x: 96, y: 740, w: 1100, h: 80,
      text: c.subline, fontFamily: "Outfit", fontSize: 44, fontWeight: 500,
      color: "#FFFFFF", align: "left", z: 5,
    }));
  }
  if (c.cta) {
    els.push(defaultElement("text", {
      id: "cta", x: 96, y: 940, w: 800, h: 60,
      text: c.cta, fontFamily: "Outfit", fontSize: 28, fontWeight: 700,
      color: "#FFFFFF", align: "left", letterSpacing: 6, z: 5,
    }));
  }
  return els;
}

function splitLayoutToElements(c) {
  const accent = c.accent_color || "#0EA5E9";
  const secondary = c.secondary_color || "#F1F5F9";
  const bg = c.background_color || "#FFFFFF";
  const text = c.text_color || "#0F172A";
  const els = [];
  els.push(defaultElement("shape", { id: "bg-left",  x: 0,   y: 0, w: 960, h: 1080, shape: "rect", fill: secondary, z: 0 }));
  els.push(defaultElement("shape", { id: "bg-right", x: 960, y: 0, w: 960, h: 1080, shape: "rect", fill: bg, z: 0 }));
  if (c.banner_image) {
    els.push(defaultElement("image", { id: "photo", x: 0, y: 0, w: 960, h: 1080, src: c.banner_image, fit: "cover", z: 1 }));
  }
  // Decorative circles like OptiSigns yoga
  els.push(defaultElement("shape", { id: "circle1", x: -120, y: 60, w: 360, h: 360, shape: "circle", fill: bg, opacity: 0.6, z: 2 }));
  els.push(defaultElement("shape", { id: "circle2", x: 700, y: 780, w: 280, h: 280, shape: "circle", fill: bg, opacity: 0.5, z: 2 }));
  if (c.logo_text) {
    els.push(defaultElement("text", {
      id: "logo", x: 1020, y: 280, w: 860, h: 60,
      text: c.logo_text, fontFamily: "Outfit", fontSize: 28, fontWeight: 700,
      color: accent, align: "center", letterSpacing: 6, z: 5,
    }));
  }
  els.push(defaultElement("text", {
    id: "headline", x: 1020, y: 360, w: 860, h: 260,
    text: c.headline || "Headline", fontFamily: "Anton", fontSize: 240, fontWeight: 900,
    color: text, align: "center", letterSpacing: -2, lineHeight: 0.9, z: 5,
  }));
  if (c.badge) {
    els.push(defaultElement("text", {
      id: "badge", x: 1020, y: 640, w: 860, h: 80,
      text: c.badge, fontFamily: "Outfit", fontSize: 60, fontWeight: 800,
      color: accent, align: "center", letterSpacing: 1, z: 5,
    }));
  }
  if (c.subline) {
    els.push(defaultElement("text", {
      id: "subline", x: 1020, y: 760, w: 860, h: 80,
      text: c.subline, fontFamily: "Pacifico", fontSize: 56, fontWeight: 400,
      color: text, align: "center", z: 5,
    }));
  }
  return els;
}

function shapeBurstToElements(c) {
  const accent = c.accent_color || "#0EA5E9";
  const secondary = c.secondary_color || "#F1F5F9";
  const bg = c.background_color || "#0F172A";
  const text = c.text_color || "#FFFFFF";
  const els = [];
  els.push(defaultElement("shape", { id: "bg", x: 0, y: 0, w: 1920, h: 1080, shape: "rect", fill: bg, z: 0 }));
  // Decorative circles
  els.push(defaultElement("shape", { id: "burst1", x: 1500, y: -220, w: 900, h: 900, shape: "circle", fill: accent, opacity: 0.18, z: 1 }));
  els.push(defaultElement("shape", { id: "burst2", x: -200, y: 700, w: 700, h: 700, shape: "circle", fill: secondary, opacity: 0.12, z: 1 }));
  if (c.banner_image) {
    els.push(defaultElement("image", { id: "photo", x: 1100, y: 200, w: 720, h: 680, src: c.banner_image, fit: "cover", borderRadius: 32, z: 2 }));
  }
  if (c.logo_text) {
    els.push(defaultElement("text", {
      id: "logo", x: 96, y: 200, w: 800, h: 50,
      text: c.logo_text, fontFamily: "Outfit", fontSize: 28, fontWeight: 700,
      color: accent, align: "left", letterSpacing: 6, z: 5,
    }));
  }
  if (c.subline) {
    els.push(defaultElement("text", {
      id: "subline", x: 96, y: 280, w: 900, h: 60,
      text: c.subline, fontFamily: "Playfair Display", italic: true, fontSize: 44, fontWeight: 500,
      color: accent, align: "left", z: 5,
    }));
  }
  els.push(defaultElement("text", {
    id: "headline", x: 96, y: 380, w: 1000, h: 320,
    text: c.headline || "Headline", fontFamily: "Anton", fontSize: 240, fontWeight: 900,
    color: text, align: "left", letterSpacing: -4, lineHeight: 0.9, z: 5,
  }));
  if (c.badge) {
    els.push(defaultElement("text", {
      id: "badge", x: 96, y: 740, w: 600, h: 80,
      text: c.badge, fontFamily: "Outfit", fontSize: 44, fontWeight: 900,
      color: bg, align: "center", letterSpacing: 1, z: 5,
    }));
  }
  return els;
}

function lifestyleCardToElements(c) {
  const accent = c.accent_color || "#0EA5E9";
  const text = c.text_color || "#FFFFFF";
  const els = [];
  if (c.banner_image) {
    els.push(defaultElement("image", { id: "bg", x: 0, y: 0, w: 1920, h: 1080, src: c.banner_image, fit: "cover", z: 0 }));
  } else {
    els.push(defaultElement("shape", { id: "bg", x: 0, y: 0, w: 1920, h: 1080, shape: "rect", fill: accent, z: 0 }));
  }
  els.push(defaultElement("shape", { id: "overlay", x: 0, y: 0, w: 1920, h: 1080, shape: "rect", fill: "#000000", opacity: 0.35, z: 1 }));
  els.push(defaultElement("shape", { id: "corner", x: 0, y: 0, w: 700, h: 450, shape: "rect", fill: accent, opacity: 1, z: 2 }));
  if (c.logo_text) {
    els.push(defaultElement("text", {
      id: "logo", x: 60, y: 280, w: 600, h: 120,
      text: c.logo_text, fontFamily: "Outfit", fontSize: 80, fontWeight: 900,
      color: text, align: "left", z: 5,
    }));
  }
  els.push(defaultElement("text", {
    id: "headline", x: 96, y: 720, w: 1700, h: 180,
    text: c.headline || "Headline", fontFamily: "Outfit", fontSize: 130, fontWeight: 900,
    color: text, align: "left", letterSpacing: -2, lineHeight: 0.95, z: 5,
  }));
  if (c.subline) {
    els.push(defaultElement("text", {
      id: "subline", x: 96, y: 900, w: 1500, h: 60,
      text: c.subline, fontFamily: "Outfit", fontSize: 44, fontWeight: 500,
      color: text, align: "left", z: 5,
    }));
  }
  return els;
}

function menuGridModernToElements(c) {
  const accent = c.accent_color || "#0EA5E9";
  const bg = c.background_color || "#FFFFFF";
  const text = c.text_color || "#0F172A";
  const els = [];
  els.push(defaultElement("shape", { id: "bg-right", x: 960, y: 0, w: 960, h: 1080, shape: "rect", fill: bg, z: 0 }));
  if (c.banner_image) {
    els.push(defaultElement("image", { id: "photo", x: 0, y: 0, w: 960, h: 1080, src: c.banner_image, fit: "cover", z: 1 }));
  }
  els.push(defaultElement("shape", { id: "photo-shade", x: 0, y: 0, w: 960, h: 1080, shape: "rect", fill: "#000000", opacity: 0.3, z: 2 }));
  if (c.logo_text) {
    els.push(defaultElement("text", {
      id: "logo", x: 60, y: 60, w: 800, h: 40,
      text: c.logo_text, fontFamily: "Outfit", fontSize: 26, fontWeight: 700,
      color: "#FFFFFF", align: "left", letterSpacing: 6, z: 5,
    }));
  }
  els.push(defaultElement("text", {
    id: "headline", x: 60, y: 880, w: 860, h: 140,
    text: c.headline || "Menu", fontFamily: "Outfit", fontSize: 96, fontWeight: 900,
    color: "#FFFFFF", align: "left", letterSpacing: -2, lineHeight: 0.95, z: 5,
  }));
  // Menu items column
  const items = (c.sections || []).flatMap((s) => s.items || []).slice(0, 6);
  items.forEach((it, i) => {
    els.push(defaultElement("text", {
      id: `item-name-${i}`, x: 1020, y: 200 + i * 130, w: 600, h: 80,
      text: it.name, fontFamily: "Outfit", fontSize: 44, fontWeight: 700,
      color: text, align: "left", z: 5,
    }));
    els.push(defaultElement("text", {
      id: `item-price-${i}`, x: 1660, y: 200 + i * 130, w: 200, h: 80,
      text: typeof it.price === "string" && it.price.match(/^[0-9.]/) ? `$${it.price}` : `${it.price}`,
      fontFamily: "Outfit", fontSize: 56, fontWeight: 900,
      color: accent, align: "right", z: 5,
    }));
  });
  return els;
}

function signageBoardToElements(c) {
  const accent = c.accent_color || "#0EA5E9";
  const els = [];
  if (c.banner_image) {
    els.push(defaultElement("image", { id: "bg", x: 0, y: 0, w: 1920, h: 1080, src: c.banner_image, fit: "cover", z: 0 }));
    els.push(defaultElement("shape", { id: "overlay", x: 0, y: 0, w: 1920, h: 1080, shape: "rect", fill: "#000000", opacity: 0.45, z: 1 }));
  } else {
    els.push(defaultElement("shape", { id: "bg", x: 0, y: 0, w: 1920, h: 1080, shape: "rect", fill: accent, z: 0 }));
  }
  if (c.headline) {
    els.push(defaultElement("text", {
      id: "headline", x: 120, y: 380, w: 1680, h: 240,
      text: c.headline, fontFamily: "Outfit", fontSize: 180, fontWeight: 900,
      color: "#FFFFFF", align: "center", letterSpacing: -3, lineHeight: 0.95, z: 5,
    }));
  }
  if (c.subline) {
    els.push(defaultElement("text", {
      id: "subline", x: 120, y: 660, w: 1680, h: 80,
      text: c.subline, fontFamily: "Outfit", fontSize: 56, fontWeight: 500,
      color: "#FFFFFF", align: "center", z: 5,
    }));
  }
  return els;
}

function defaultToElements(c) {
  const bg = c.background_color || "#0F172A";
  const accent = c.accent_color || "#0EA5E9";
  const text = c.text_color || "#FFFFFF";
  return [
    defaultElement("shape", { id: "bg", x: 0, y: 0, w: 1920, h: 1080, shape: "rect", fill: bg, z: 0 }),
    defaultElement("text", {
      id: "headline", x: 120, y: 460, w: 1680, h: 200,
      text: c.business_name || c.title || "Your design", fontFamily: "Outfit", fontSize: 140, fontWeight: 900,
      color: text, align: "center", z: 5,
    }),
    defaultElement("text", {
      id: "accent", x: 120, y: 660, w: 1680, h: 80,
      text: c.subtitle || "Tap to edit", fontFamily: "Outfit", fontSize: 48, fontWeight: 500,
      color: accent, align: "center", z: 5,
    }),
  ];
}


// ===========================================================================
// MARBLE BOARD (burger_combo_kids) → editable elements
// ===========================================================================
// Original layout in PolishedRenderers.BurgerComboKidsRender uses vw-based
// sizing on a 16:9 stage. Inside a 1920x1080 canvas, 1vw ≈ 19.2px and
// 1vh ≈ 10.8px. We transcribe the layout to absolute pixel coordinates so
// each visible element becomes independently click-and-drag editable.
function marbleBoardToElements(c) {
  const vw = 19.2, vh = 10.8;
  const els = [];

  const accent      = c.accent_color || "#FFA600";
  const priceColor  = c.price_color || c.secondary_color || "#D11D1D";
  const bg          = c.background_color || "#1B1B1F";
  const theme       = c.theme || {};
  const bgFrom = theme.bg_from || "#1F1F22";
  const bgVia  = theme.bg_via  || "#0A0A0C";
  const bgTo   = theme.bg_to   || "#18181B";
  const hi1    = theme.highlight_1 || "rgba(85,85,90,0.45)";
  const hi2    = theme.highlight_2 || "rgba(60,60,65,0.55)";
  const hi3    = theme.highlight_3 || "rgba(80,80,85,0.32)";
  const veinA  = theme.vein_a  || "rgba(255,255,255,0.05)";
  const veinB  = theme.vein_b  || "rgba(255,255,255,0.035)";

  // Marble background fill — one giant locked shape stacking all gradient layers
  const marbleBg =
    `repeating-linear-gradient(118deg, ${veinA} 0 1px, transparent 1px 28px), ` +
    `repeating-linear-gradient(62deg, ${veinB} 0 1px, transparent 1px 41px), ` +
    `radial-gradient(ellipse 60% 40% at 20% 15%, ${hi1} 0%, transparent 55%), ` +
    `radial-gradient(ellipse 55% 45% at 85% 55%, ${hi2} 0%, transparent 60%), ` +
    `radial-gradient(ellipse 70% 55% at 45% 95%, ${hi3} 0%, transparent 60%), ` +
    `linear-gradient(135deg, ${bgFrom} 0%, ${bgVia} 50%, ${bgTo} 100%)`;
  els.push(defaultElement("shape", {
    id: "marble-bg", x: 0, y: 0, w: 1920, h: 1080, shape: "rect",
    fill: marbleBg, locked: true, z: 0,
  }));

  // ====== Yellow side panel (right 22%) ======
  const panelX = Math.round(1920 * 0.78);   // = 1497.6 → 1498
  const panelW = 1920 - panelX;             // = 422
  const kids = c.kids_panel || {};
  const sideBg = kids.panel_color || accent;
  els.push(defaultElement("shape", {
    id: "side-panel-bg", x: panelX, y: 0, w: panelW, h: 1080, shape: "rect",
    fill: sideBg, locked: true, z: 1,
  }));

  // Watermark stripes (skewed italic text repeated diagonally)
  const watermarkText = kids.watermark_text || "BURGER DEAL ©";
  const watermarkColor = kids.watermark || "#C8141A";
  for (let i = 0; i < 6; i++) {
    els.push(defaultElement("text", {
      id: `side-watermark-${i}`,
      x: panelX - 20,
      y: Math.round(1080 * (0.56 + i * 0.07)),
      w: panelW + 40,
      h: 70,
      text: watermarkText,
      fontFamily: "Anton", fontSize: 46, fontWeight: 900,
      italic: true,
      color: watermarkColor,
      align: "left",
      letterSpacing: 1.5,
      skewX: -8,
      z: 2,
    }));
  }

  // Chef logo
  els.push(defaultElement("image", {
    id: "side-chef-logo",
    x: panelX + Math.round((panelW - 96) / 2),
    y: 38,
    w: 96, h: 96,
    src: "/icons/chef-hat.svg",
    fit: "contain",
    z: 3,
  }));

  // Side eyebrow (cursive)
  const panelDark = kids.text_dark || "#0A0A0A";
  els.push(defaultElement("text", {
    id: "side-eyebrow",
    x: panelX, y: 145, w: panelW, h: 50,
    text: kids.eyebrow || c.eyebrow || "Fast Food",
    fontFamily: "Playfair Display", italic: true,
    fontSize: 32, fontWeight: 500,
    color: panelDark, align: "center", z: 4,
  }));

  // Side line1 (big solid)
  els.push(defaultElement("text", {
    id: "side-line1",
    x: panelX, y: 200, w: panelW, h: 80,
    text: kids.line1 || "BURGER",
    fontFamily: "Anton", fontSize: 72, fontWeight: 900,
    color: panelDark, align: "center", letterSpacing: 1,
    lineHeight: 0.9, z: 5,
  }));

  // Side line2 (outlined italic)
  const panelOutline = kids.text_outline || "#FFFFFF";
  els.push(defaultElement("text", {
    id: "side-line2",
    x: panelX, y: 280, w: panelW, h: 80,
    text: kids.line2 || "MEAL",
    fontFamily: "Anton", fontSize: 72, fontWeight: 900,
    italic: true,
    color: "transparent",
    textStrokeWidth: 3,
    textStrokeColor: panelOutline,
    align: "center", letterSpacing: 1,
    lineHeight: 0.9, skewX: -6, z: 6,
  }));

  // Side price tag (white pill rotated -12deg, with price text on top)
  const priceTagBg   = kids.price_bg   || "#FFFFFF";
  const priceTagText = kids.price_text || "#0A0A0A";
  const priceTagX = panelX + Math.round((panelW - 230) / 2);
  const priceTagY = 400;
  els.push(defaultElement("shape", {
    id: "side-price-bg",
    x: priceTagX, y: priceTagY, w: 230, h: 90,
    shape: "rect", fill: priceTagBg, rotation: -12, z: 7,
  }));
  els.push(defaultElement("text", {
    id: "side-price-text",
    x: priceTagX, y: priceTagY, w: 230, h: 90,
    text: `$${kids.price || "6.99"}`,
    fontFamily: "Anton", fontSize: 56, fontWeight: 900,
    italic: true, color: priceTagText, align: "center",
    rotation: -12, z: 8,
  }));

  // Bottom meal photo (if present)
  if (kids.image) {
    els.push(defaultElement("image", {
      id: "side-meal-photo",
      x: panelX + 20,
      y: 780,
      w: panelW - 40, h: 280,
      src: kids.image,
      fit: "contain",
      dropShadow: "drop-shadow(0 18px 24px rgba(0,0,0,0.45))",
      z: 9,
    }));
  }

  // ====== Header (main area title row) ======
  const mainPad = 60;
  const mainW = panelX - mainPad * 2;
  const centerX = mainPad + Math.round(mainW / 2);

  // Burger silhouettes (left + right of title)
  els.push(defaultElement("image", {
    id: "title-burger-left",
    x: centerX - 290, y: 70, w: 96, h: 96,
    src: "/icons/burger.svg", fit: "contain", z: 5,
  }));
  els.push(defaultElement("image", {
    id: "title-burger-right",
    x: centerX + 194, y: 70, w: 96, h: 96,
    src: "/icons/burger.svg", fit: "contain", z: 5,
  }));

  // Eyebrow (cursive white)
  els.push(defaultElement("text", {
    id: "title-eyebrow",
    x: centerX - 180, y: 55, w: 360, h: 50,
    text: c.eyebrow || "Fast Food",
    fontFamily: "Playfair Display", italic: true,
    fontSize: 38, fontWeight: 500,
    color: "#FFFFFF", align: "center", z: 5,
  }));

  // Headline (big accent color)
  els.push(defaultElement("text", {
    id: "title-headline",
    x: centerX - 360, y: 100, w: 720, h: 110,
    text: c.headline || "BURGER MENU",
    fontFamily: "Anton", fontSize: 96, fontWeight: 900,
    color: accent, align: "center",
    letterSpacing: 1.4, lineHeight: 0.95, z: 5,
  }));

  // ====== 4×2 floating items grid ======
  const items = (c.items || []).slice(0, 8);
  const cols = 4;
  const gridY = 240;
  const gridH = 760;   // until ~y=1000
  const colGap = 30;
  const rowGap = 32;
  const cellW = Math.floor((mainW - colGap * (cols - 1)) / cols);
  const rowH  = Math.floor((gridH - rowGap) / 2);

  items.forEach((item, i) => {
    const row = Math.floor(i / cols);
    const col = i % cols;
    const cellX = mainPad + col * (cellW + colGap);
    const cellY = gridY + row * (rowH + rowGap);

    // Image (transparent PNG) — ~67% of cell height
    const imgH = Math.round(rowH * 0.55);
    const imgW = Math.round(cellW * 0.9);
    const imgX = cellX + Math.round((cellW - imgW) / 2);
    els.push(defaultElement("image", {
      id: `item-${i}-img`,
      x: imgX, y: cellY, w: imgW, h: imgH,
      src: item.image || "",
      fit: "contain",
      dropShadow: "drop-shadow(0 22px 28px rgba(0,0,0,0.7))",
      z: 10 + i * 5,
    }));

    // Red circle price badge — top-right of cell
    const badgeSize = 86;
    els.push(defaultElement("shape", {
      id: `item-${i}-badge-bg`,
      x: cellX + cellW - badgeSize - 8,
      y: cellY - 12,
      w: badgeSize, h: badgeSize,
      shape: "circle", fill: priceColor, z: 11 + i * 5,
    }));
    els.push(defaultElement("text", {
      id: `item-${i}-price`,
      x: cellX + cellW - badgeSize - 8,
      y: cellY - 12,
      w: badgeSize, h: badgeSize,
      text: `$${item.price || ""}`,
      fontFamily: "Anton", italic: true,
      fontSize: 30, fontWeight: 900,
      color: "#FFFFFF", align: "center", z: 12 + i * 5,
    }));

    // Item name (yellow) — below image
    const nameY = cellY + imgH + 14;
    els.push(defaultElement("text", {
      id: `item-${i}-name`,
      x: cellX, y: nameY, w: cellW, h: 70,
      text: String(item.name || "").replace(/\n/g, " "),
      fontFamily: "Anton", fontSize: 26, fontWeight: 900,
      color: accent, align: "center",
      letterSpacing: 1.2, lineHeight: 1.1, z: 13 + i * 5,
    }));

    // Item description (italic white)
    if (item.description) {
      els.push(defaultElement("text", {
        id: `item-${i}-desc`,
        x: cellX, y: nameY + 78, w: cellW, h: 60,
        text: item.description,
        fontFamily: "Playfair Display", italic: true,
        fontSize: 18, fontWeight: 500,
        color: "#F1F1F4", align: "center", lineHeight: 1.25,
        z: 14 + i * 5,
      }));
    }
  });

  // ====== Social pills ======
  if (c.facebook_handle || c.instagram_handle) {
    const socialY = 1010;
    const handles = [
      c.facebook_handle  && { kind: "fb", text: c.facebook_handle },
      c.instagram_handle && { kind: "ig", text: c.instagram_handle },
    ].filter(Boolean);
    handles.forEach((h, i) => {
      const baseX = centerX - (handles.length * 200) / 2 + i * 200;
      els.push(defaultElement("text", {
        id: `social-${h.kind}`,
        x: baseX, y: socialY, w: 200, h: 40,
        text: `@${h.text.replace(/^@/, "")}`,
        fontFamily: "Playfair Display", italic: true,
        fontSize: 22, fontWeight: 500,
        color: "#FFFFFF", align: "center", z: 50,
      }));
    });
  }

  return els;
}

// ===========================================================================
// CHALKBOARD GRID → editable elements
// ===========================================================================
function chalkboardGridToElements(c) {
  const els = [];
  const accent  = c.accent_color || "#FBBF24";
  const surface = c.secondary_color || "#0F0F0F";
  const bg      = c.background_color || "#0A0A0A";
  const text    = c.text_color || "#FFFFFF";

  // Chalkboard slate background (radial spotlight + dual slate noise)
  const chalkBg =
    `repeating-linear-gradient(45deg, rgba(255,255,255,0.05) 0 1px, transparent 1px 6px), ` +
    `repeating-linear-gradient(-45deg, rgba(255,255,255,0.035) 0 1px, transparent 1px 9px), ` +
    `radial-gradient(ellipse 70% 60% at 50% 40%, ${surface} 0%, ${bg} 70%, #050505 100%)`;
  els.push(defaultElement("shape", {
    id: "chalk-bg", x: 0, y: 0, w: 1920, h: 1080, shape: "rect",
    fill: chalkBg, locked: true, z: 0,
  }));

  // Yellow outer frame (no fill, just a thick border rectangle drawn via stroke)
  els.push(defaultElement("shape", {
    id: "chalk-frame", x: 28, y: 28, w: 1864, h: 1024, shape: "rect",
    fill: "transparent", stroke: `${accent}AA`, strokeWidth: 5, borderRadius: 8,
    locked: true, z: 1,
  }));

  // Logo (top-right small caps)
  if (c.logo_text) {
    els.push(defaultElement("text", {
      id: "chalk-logo",
      x: 1300, y: 48, w: 560, h: 40,
      text: c.logo_text,
      fontFamily: "Anton", fontSize: 26, fontWeight: 900,
      color: accent, align: "right", letterSpacing: 4, z: 2,
    }));
  }

  // Headline (big — accent word manually editable as second text)
  const headline = c.headline || "MENU";
  const headlineAccent = c.headline_accent || "";
  const accentIdx = headlineAccent
    ? headline.toLowerCase().lastIndexOf(headlineAccent.toLowerCase())
    : -1;
  if (accentIdx >= 0) {
    const before = headline.slice(0, accentIdx);
    const accentPart = headline.slice(accentIdx, accentIdx + headlineAccent.length);
    els.push(defaultElement("text", {
      id: "chalk-headline-1",
      x: 80, y: 78, w: 1100, h: 140,
      text: before,
      fontFamily: "Anton", fontSize: 134, fontWeight: 900,
      color: text, align: "left", letterSpacing: -1, lineHeight: 0.9, z: 3,
    }));
    els.push(defaultElement("text", {
      id: "chalk-headline-2",
      x: 80 + 18 * Math.max(1, before.length), y: 78, w: 1100, h: 140,
      text: accentPart,
      fontFamily: "Anton", fontSize: 134, fontWeight: 900,
      color: accent, align: "left", letterSpacing: -1, lineHeight: 0.9, z: 4,
    }));
  } else {
    els.push(defaultElement("text", {
      id: "chalk-headline",
      x: 80, y: 78, w: 1760, h: 140,
      text: headline,
      fontFamily: "Anton", fontSize: 134, fontWeight: 900,
      color: text, align: "left", letterSpacing: -1, lineHeight: 0.9, z: 3,
    }));
  }
  if (c.subline) {
    els.push(defaultElement("text", {
      id: "chalk-subline",
      x: 80, y: 230, w: 1760, h: 50,
      text: c.subline,
      fontFamily: "Outfit", fontSize: 32, fontWeight: 500,
      color: text, align: "left", z: 4,
    }));
  }

  // ====== Top row of items (up to 8) ======
  const sections = c.sections || [];
  const topItems = (sections[0]?.items || []).slice(0, 8);
  const topY = 300;
  const topPad = 80;
  const topW = 1920 - topPad * 2;
  const dividerW = 12;
  const colCount = Math.max(1, topItems.length);
  const colW = Math.floor((topW - dividerW * (colCount - 1)) / colCount);
  topItems.forEach((it, i) => {
    const x = topPad + i * (colW + dividerW);

    // Name
    els.push(defaultElement("text", {
      id: `chalk-top-${i}-name`,
      x: x, y: topY, w: colW, h: 80,
      text: it.name || "",
      fontFamily: "Anton", fontSize: 28, fontWeight: 900,
      color: it.accent_word ? text : text,
      align: "center", letterSpacing: 0.5, lineHeight: 1.05, z: 5,
    }));

    // Price ribbon (yellow skewed)
    const priceFormatted = String(it.price || "").match(/^[\d.]+$/) ? `£${it.price}` : (it.price || "");
    els.push(defaultElement("shape", {
      id: `chalk-top-${i}-price-bg`,
      x: x + Math.round((colW - 110) / 2),
      y: topY + 92,
      w: 110, h: 40,
      shape: "rect", fill: accent, skewX: -12, z: 6,
    }));
    els.push(defaultElement("text", {
      id: `chalk-top-${i}-price`,
      x: x + Math.round((colW - 110) / 2),
      y: topY + 92, w: 110, h: 40,
      text: priceFormatted,
      fontFamily: "Anton", fontSize: 28, fontWeight: 900,
      color: "#0A0A0A", align: "center", z: 7,
    }));

    // Image (item photo, square contain)
    if (it.image) {
      const photoSize = Math.min(colW - 20, 130);
      els.push(defaultElement("image", {
        id: `chalk-top-${i}-img`,
        x: x + Math.round((colW - photoSize) / 2),
        y: topY + 150,
        w: photoSize, h: photoSize,
        src: it.image, fit: "contain",
        dropShadow: "drop-shadow(0 16px 18px rgba(0,0,0,0.65))",
        z: 8,
      }));
    }

    // Dotted vertical divider before each item except first
    if (i > 0) {
      els.push(defaultElement("text", {
        id: `chalk-top-${i}-divider`,
        x: x - dividerW - 2, y: topY + 30, w: dividerW + 4, h: 200,
        text: "·\n·\n·\n·\n·\n·\n·\n·\n·\n·",
        fontFamily: "Outfit", fontSize: 18, fontWeight: 800,
        color: "#FFFFFF", align: "center", lineHeight: 1.0,
        z: 4, locked: false,
      }));
    }
  });

  // ====== Bottom two panels ======
  const bottomLeft  = sections[1];
  const bottomRight = sections[2];
  const bottomY = 580;
  const bottomH = 460;
  const panelGap = 40;
  const panelWidth = Math.floor((1920 - topPad * 2 - panelGap) / 2);
  [bottomLeft, bottomRight].forEach((sec, idx) => {
    if (!sec) return;
    const px = topPad + idx * (panelWidth + panelGap);

    // Panel background
    els.push(defaultElement("shape", {
      id: `chalk-panel-${idx}-bg`,
      x: px, y: bottomY, w: panelWidth, h: bottomH,
      shape: "rect", fill: "rgba(255,255,255,0.04)",
      stroke: `${accent}99`, strokeWidth: 3, borderRadius: 12,
      locked: false, z: 3,
    }));

    // Panel title
    els.push(defaultElement("text", {
      id: `chalk-panel-${idx}-title`,
      x: px + 36, y: bottomY + 24, w: panelWidth - 200, h: 90,
      text: sec.title || "",
      fontFamily: "Anton", fontSize: 76, fontWeight: 900,
      color: text, align: "left", lineHeight: 0.9, letterSpacing: -0.5, z: 5,
    }));

    // Flagship price ribbon (if present)
    if (sec.flagship_price) {
      els.push(defaultElement("shape", {
        id: `chalk-panel-${idx}-flag-bg`,
        x: px + panelWidth - 230, y: bottomY + 30, w: 200, h: 50,
        shape: "rect", fill: accent, skewX: -12, z: 6,
      }));
      els.push(defaultElement("text", {
        id: `chalk-panel-${idx}-flag`,
        x: px + panelWidth - 230, y: bottomY + 30, w: 200, h: 50,
        text: sec.flagship_price,
        fontFamily: "Anton", fontSize: 24, fontWeight: 900,
        color: "#0A0A0A", align: "center", z: 7,
      }));
    }

    // Panel items
    (sec.items || []).slice(0, 6).forEach((it, j) => {
      const iy = bottomY + 130 + j * 50;
      els.push(defaultElement("text", {
        id: `chalk-panel-${idx}-item-${j}-name`,
        x: px + 36, y: iy, w: panelWidth - 200, h: 40,
        text: it.description ? `${it.name || ""} — ${it.description}` : (it.name || ""),
        fontFamily: "Anton", fontSize: 26, fontWeight: 900,
        color: text, align: "left", letterSpacing: 0.5, z: 5,
      }));
      if (it.price) {
        const pf = String(it.price).match(/^[\d.]+$/) ? `£${it.price}` : it.price;
        els.push(defaultElement("shape", {
          id: `chalk-panel-${idx}-item-${j}-price-bg`,
          x: px + panelWidth - 130, y: iy + 2, w: 100, h: 36,
          shape: "rect", fill: accent, skewX: -12, z: 6,
        }));
        els.push(defaultElement("text", {
          id: `chalk-panel-${idx}-item-${j}-price`,
          x: px + panelWidth - 130, y: iy + 2, w: 100, h: 36,
          text: pf,
          fontFamily: "Anton", fontSize: 22, fontWeight: 900,
          color: "#0A0A0A", align: "center", z: 7,
        }));
      }
    });
  });

  // Footnote
  if (c.footnote) {
    els.push(defaultElement("text", {
      id: "chalk-footnote",
      x: 1300, y: 1020, w: 560, h: 30,
      text: c.footnote,
      fontFamily: "Playfair Display", italic: true,
      fontSize: 18, fontWeight: 400,
      color: "rgba(255,255,255,0.55)", align: "right", z: 4,
    }));
  }

  return els;
}
